import { T, useGT } from 'gt-react';
import { useWizard } from '@erp/ui/hooks/use-wizard';
import type { ValidationRuleDTO } from '@erp/rpc';
import {
  Wizard,
  WizardFooter,
  WizardHeader,
  WizardPanel,
  WizardRail,
  WizardSurface,
} from '@/components/ui/wizard';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AssignmentPanel,
  ConsistentFieldsPanel,
  FiltersPanel,
  GroupingPanel,
  NormalizationPanel,
  PreviewPanel,
  SavePanel,
  SchemaPanel,
  StrategyPanel,
} from './ValidationRuleWizardPanels';
import { buildValidationRuleWizardSteps } from './buildValidationRuleWizardSteps';
import { useValidationRuleWizardState } from './useValidationRuleWizardState';

export interface ValidationRuleWizardProps {
  /** The rule to edit. Omit (or null) to author a new one. */
  rule?: ValidationRuleDTO | null;
  /** Fired with the saved rule's id once the save lands. */
  onSaved: (ruleId: string) => void;
  onCancel: () => void;
}

/**
 * The guided rule builder — no free-form SQL anywhere in it:
 *
 *   1. Table        — one of the org's scannable logical schemas
 *   2. Row filters  — optional, the catalog's own advanced-filter editor
 *   3. Grouping     — which rows are compared against each other
 *   4. Must agree   — which fields have to match within a group
 *   5. Comparison   — the three normalization flags
 *   6. Proposed fix — the correction strategy, and its division when needed
 *   7. Owner        — assignee and trigger
 *   8. Preview      — evaluates the unsaved definition, read-only
 *   9. Save         — as a draft or active (gate)
 *
 * Steps 3, 4 and 6 mirror the server's own definition rules, so an author is
 * stopped on the step that owns the mistake rather than by a 400 from the save.
 *
 * Everything but the layout lives elsewhere — state and side effects in
 * `useValidationRuleWizardState`, the step list in
 * `buildValidationRuleWizardSteps`, step bodies in
 * `ValidationRuleWizardPanels`, and the step branching in
 * `validationRuleWizardChecks`.
 */
export function ValidationRuleWizard({
  rule = null,
  onSaved,
  onCancel,
}: ValidationRuleWizardProps) {
  const gt = useGT();
  const state = useValidationRuleWizardState({ rule, onSaved });
  const wizard = useWizard({
    onComplete: state.handleComplete,
    steps: buildValidationRuleWizardSteps(state, gt),
    // Arriving at the preview evaluates the rule, so the author reads what it
    // found before deciding to continue rather than after.
    onStepChange: (stepId) => {
      if (stepId !== 'preview') return;
      if (state.previewing || state.previewedKey === state.definitionKey)
        return;
      void state.runPreview();
    },
  });

  return (
    <Wizard api={wizard} className="h-[640px]">
      <WizardRail />
      <WizardSurface>
        <WizardHeader />
        <div className="flex-1 overflow-y-auto px-5 py-5">
          <WizardPanel stepId="schema">
            <SchemaPanel
              schemas={state.schemas}
              schemaName={state.schemaName}
              onSelect={state.selectSchema}
              isLoading={state.schemasLoading}
              loadError={state.schemasError}
              editing={state.mode === 'edit'}
            />
          </WizardPanel>

          <WizardPanel stepId="filters">
            <FiltersPanel
              schema={state.selectedSchema}
              rowFilters={state.rowFilters}
              onChange={state.setRowFilters}
            />
          </WizardPanel>

          <WizardPanel stepId="grouping">
            <GroupingPanel
              schema={state.selectedSchema}
              groupingFields={state.groupingFields}
              onToggle={state.toggleGroupingField}
            />
          </WizardPanel>

          <WizardPanel stepId="consistent">
            <ConsistentFieldsPanel
              schema={state.selectedSchema}
              groupingFields={state.groupingFields}
              validatedFields={state.validatedFields}
              overlappingFields={state.overlappingFields}
              onToggle={state.toggleValidatedField}
            />
          </WizardPanel>

          <WizardPanel stepId="normalization">
            <NormalizationPanel
              normalization={state.normalization}
              onChange={state.setNormalizationFlag}
            />
          </WizardPanel>

          <WizardPanel stepId="strategy">
            <StrategyPanel
              correctionStrategy={state.correctionStrategy}
              onStrategyChange={state.setCorrectionStrategy}
              divisions={state.divisions}
              preferredDataSourceId={state.preferredDataSourceId}
              onPreferredChange={state.setPreferredDataSourceId}
            />
          </WizardPanel>

          <WizardPanel stepId="assignment">
            <AssignmentPanel
              assigneeUserId={state.assigneeUserId}
              onAssigneeChange={state.setAssigneeUserId}
              executionTrigger={state.executionTrigger}
              onTriggerChange={state.setExecutionTrigger}
              scheduleCron={state.scheduleCron}
              onScheduleCronChange={state.setScheduleCron}
            />
          </WizardPanel>

          <WizardPanel stepId="preview">
            <PreviewPanel
              preview={state.preview}
              previewError={state.previewError}
              previewing={state.previewing}
              onRunPreview={() => void state.runPreview()}
              hasPreviewed={state.previewedKey !== null}
            />
          </WizardPanel>

          <WizardPanel stepId="save">
            <SavePanel
              name={state.name}
              onNameChange={state.setName}
              description={state.description}
              onDescriptionChange={state.setDescription}
              activateOnSave={state.activateOnSave}
              onActivateChange={state.setActivateOnSave}
              canChooseStatus={state.canChooseStatus}
              saveError={state.saveError}
              violatingGroups={state.preview?.stats.violatingGroups ?? null}
            />
          </WizardPanel>
        </div>

        <WizardFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            disabled={state.busy}
          >
            <T context="button to abandon the validation rule builder">
              Cancel
            </T>
          </Button>
        </WizardFooter>
      </WizardSurface>
    </Wizard>
  );
}

export interface ValidationRuleWizardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The rule to edit. Omit (or null) to author a new one. */
  rule?: ValidationRuleDTO | null;
  /** Fired with the saved rule's id after the dialog closes itself. */
  onSaved?: (ruleId: string) => void;
}

/**
 * The rule builder in a dialog — the entry point every caller uses. The wizard
 * mounts only while open, so each open starts from the rule it was handed
 * (or from nothing) instead of from the last session's leftovers.
 */
export function ValidationRuleWizardDialog({
  open,
  onOpenChange,
  rule = null,
  onSaved,
}: ValidationRuleWizardDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl sm:max-w-5xl p-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5">
          <DialogTitle>
            {rule ? (
              <T context="dialog title when editing an existing validation rule">
                Edit Rule
              </T>
            ) : (
              <T context="dialog title for the new validation rule builder">
                New Rule
              </T>
            )}
          </DialogTitle>
        </DialogHeader>
        {open && (
          <ValidationRuleWizard
            key={rule?.id ?? 'new'}
            rule={rule}
            onCancel={() => onOpenChange(false)}
            onSaved={(ruleId) => {
              onOpenChange(false);
              onSaved?.(ruleId);
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
